/**
 * Single source of truth for export-docs stage labels, variants, doc-type
 * labels, and operator-allowed transitions.
 *
 * Mirrors the backend constants 1:1:
 *   - `middleware/app/models/export_docs_packet.py` (ALL_STAGES, ALL_DOC_TYPES,
 *     TERMINAL_STAGES)
 *   - `middleware/app/services/export_docs_pipeline.py` (ALLOWED_TRANSITIONS)
 *
 * Unlike sales-inbox's permissive "any non-terminal to any other" kanban,
 * this pipeline is deliberately LINEAR/NARROW — a trading-intermediary
 * approval gate is a compliance event worth requiring a real transition
 * for, not a casual lateral drag. Do not widen this table without also
 * widening the Python ALLOWED_TRANSITIONS frozenset; a test
 * (`stage-meta.test.ts`) pins the two in sync.
 */

import type { DocType, Stage } from '../api/types'

type ChipVariant = 'neutral' | 'success' | 'warning' | 'danger' | 'info'

export const STAGES: readonly Stage[] = [
  'draft_generating',
  'draft_ready',
  'draft_regenerating',
  'sent_to_intermediary',
  'approved',
  'finals_generating',
  'finals_ready',
  'generation_failed',
  'rejected',
  'cancelled',
] as const

export const TERMINAL_STAGES: readonly Stage[] = ['finals_ready', 'cancelled'] as const

/** Stages shown as kanban columns. Both terminal stages get their own
 *  column (unlike sales-inbox, which archives terminals off the board) —
 *  packet volume is low enough that seeing "done" and "cancelled" loads
 *  next to active ones is useful, matching the freight-pipeline
 *  precedent rather than the sales-inbox one. */
export const BOARD_STAGES: readonly Stage[] = STAGES

export const STAGE_LABELS: Record<Stage, string> = {
  draft_generating: 'Generating Drafts',
  draft_ready: 'Draft Ready',
  draft_regenerating: 'Regenerating Drafts',
  sent_to_intermediary: 'Sent to Intermediary',
  approved: 'Approved',
  finals_generating: 'Generating Finals',
  finals_ready: 'Finals Ready',
  generation_failed: 'Generation Failed',
  rejected: 'Rejected',
  cancelled: 'Cancelled',
}

export const STAGE_SHORT_LABELS: Record<Stage, string> = {
  draft_generating: 'Generating',
  draft_ready: 'Draft Ready',
  draft_regenerating: 'Regenerating',
  sent_to_intermediary: 'Sent',
  approved: 'Approved',
  finals_generating: 'Finalizing',
  finals_ready: 'Finals Ready',
  generation_failed: 'Failed',
  rejected: 'Rejected',
  cancelled: 'Cancelled',
}

export const STAGE_VARIANT: Record<Stage, ChipVariant> = {
  draft_generating: 'neutral',
  draft_ready: 'info',
  draft_regenerating: 'neutral',
  sent_to_intermediary: 'warning',
  approved: 'success',
  finals_generating: 'warning',
  finals_ready: 'success',
  generation_failed: 'danger',
  rejected: 'danger',
  cancelled: 'neutral',
}

export interface Transition {
  from: Stage
  to: Stage
}

const NON_TERMINAL_STAGES = STAGES.filter(
  (s) => !TERMINAL_STAGES.includes(s as (typeof TERMINAL_STAGES)[number]),
)

// Explicit edges — 1:1 with `_EXPLICIT_TRANSITIONS` in
// `export_docs_pipeline.py`.
const EXPLICIT_TRANSITIONS: readonly Transition[] = [
  { from: 'draft_generating', to: 'draft_ready' },
  { from: 'draft_generating', to: 'generation_failed' },
  { from: 'draft_ready', to: 'draft_regenerating' },
  { from: 'draft_ready', to: 'sent_to_intermediary' },
  { from: 'draft_regenerating', to: 'draft_ready' },
  { from: 'draft_regenerating', to: 'generation_failed' },
  { from: 'sent_to_intermediary', to: 'approved' },
  { from: 'sent_to_intermediary', to: 'rejected' },
  { from: 'rejected', to: 'draft_generating' },
  { from: 'approved', to: 'finals_generating' },
  { from: 'finals_generating', to: 'finals_ready' },
  { from: 'finals_generating', to: 'generation_failed' },
  { from: 'generation_failed', to: 'draft_generating' },
]

// cancelled is reachable from any non-terminal stage — mirrors the
// Python frozenset's additive `(stage, STAGE_CANCELLED)` comprehension.
export const ALLOWED_TRANSITIONS: readonly Transition[] = [
  ...EXPLICIT_TRANSITIONS,
  ...NON_TERMINAL_STAGES.map<Transition>((from) => ({ from, to: 'cancelled' })),
] as const

export function isTerminal(stage: Stage): boolean {
  return TERMINAL_STAGES.includes(stage as (typeof TERMINAL_STAGES)[number])
}

export function transitionAllowed(from: Stage, to: Stage): boolean {
  return ALLOWED_TRANSITIONS.some((t) => t.from === from && t.to === to)
}

export function allowedTargetsFrom(stage: Stage): Stage[] {
  return ALLOWED_TRANSITIONS.filter((t) => t.from === stage).map((t) => t.to)
}

// ─── doc_type meta ─────────────────────────────────────────────────────

export const ALL_DOC_TYPES: readonly DocType[] = [
  'proforma_invoice',
  'commercial_invoice',
  'packing_list',
  'sli',
  'cofa',
  'sds',
  'certificate_of_origin',
  'dg_declaration',
  'carton_labels',
  'bol_instructions',
] as const

export const DOC_TYPE_LABELS: Record<DocType, string> = {
  proforma_invoice: 'Proforma Invoice',
  commercial_invoice: 'Commercial Invoice',
  packing_list: 'Packing List',
  sli: 'Shipping Letter of Instruction',
  cofa: 'Certificate of Analysis',
  sds: 'Safety Data Sheet',
  certificate_of_origin: 'Certificate of Origin',
  dg_declaration: 'Dangerous Goods Declaration',
  carton_labels: 'Carton Labels',
  bol_instructions: 'BL / AWB Instructions',
}

/** Doc types bundled from an existing Odoo document rather than rendered
 *  fresh — mirrors `BUNDLED_DOC_TYPES` in export_docs_packet.py. */
export const BUNDLED_DOC_TYPES: readonly DocType[] = ['cofa', 'sds'] as const

export function isBundledDocType(docType: DocType): boolean {
  return BUNDLED_DOC_TYPES.includes(docType)
}
